import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { AdminNavEntryPointV3, NavEntryPointV3 } from '@retailer-platform/dashboard/routing'
import { retailerFundedMarketingRouter } from './routing/retailerFundedMarketingRouter'
import { retailerFundedMarketingAdminRouter } from './routing/retailerFundedMarketingAdminRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import {
  retailerFundedMarketingHomeAccessControl,
  retailerFundedMarketingHomeNavAccessControl,
} from './access-control/retailerFundedMarketingHomeAccess.configuration'
import { retailerFundedMarketingSegmentationAccessControl } from './access-control/retailerFundedMarketingTargetingAccess.configuration'
import { marketingAgentCalendarUnionAccessControl } from './access-control/marketingAgentCalendarAccess.configuration'
import { retailerFundedMarketingV2Router } from './routing/retailerFundedMarketingV2Router'
import {
  retailerFundedMarketingAdminAccessControl,
  retailerFundedMarketingAdminCustomCampaignAccessControl,
} from './access-control/retailerFundedMarketingAdminAccess.configuration'
import { globalCampaignsAdminRouter } from './routing/GlobalCampaignsAdminRouter'
import { campaignsAdminRouter } from './routing/campaignsAdminRouter'
import { marketingAgentAdminRouter } from './routing/marketingAgentAdminRouter'
import { retailerFundedMarketingWholesalerAccessControl } from './access-control/retailerFundedMarketingWholesalerAccess.configuration'
import { retailerFundedMarketingPartnerScopedRouter } from './routing/retailerFundedMarketingPartnerScopedRouter'
import { retailerFundedMarketingPartnerScopedSegmentRouter } from './routing/retailerFundedMarketingPartnerScopedSegmentRouter'
import { lifecycleCrmAccessControl } from './access-control/lifecycleCrmAccess.configuration'
import { sfpCampaignAndSegmentAccessControl } from './access-control/sfpCampaignAndSegmentAccess.configuration'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'retailer-funded-marketing',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'retailer-funded-marketing-dashboard-tool',
  },
  routes,
  translations: {},
  lazyTranslations: {
    EN_US: () => import('./translations/en-US.json'),
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [retailerFundedMarketingV2Router, retailerFundedMarketingRouter],
    partnerScoped: [
      retailerFundedMarketingPartnerScopedRouter,
      retailerFundedMarketingPartnerScopedSegmentRouter,
    ],
    admin: [
      retailerFundedMarketingAdminRouter,
      globalCampaignsAdminRouter,
      campaignsAdminRouter,
      marketingAgentAdminRouter,
    ],
  },
  navigationEntries: [
    {
      attachToV3: NavEntryPointV3.Storefront,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Create and manage retailer-funded marketing campaigns',
          // Shared with the SFP L1 entry; SF and SFP L1s are mutually exclusive per retailer.
          labelId: 'retailerFundedMarketingDomain.campaigns.phaseFour.nav',
          labelIdV3: 'retailerFundedMarketingDomain.campaigns.phaseFour.nav.v3',
          route: 'retailer-funded-marketing-site-scoped-list-storefront',
          accessControl: retailerFundedMarketingHomeNavAccessControl,
          positionNavV3: 0,
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage marketing campaigns exclusively for your custom Storefront Pro platform',
          // Mirrored under the SF L1 so SF and SFP present an identical section;
          // sfpCampaignAndSegmentAccessControl gates it to Storefront Pro users only.
          labelId: 'retailerFundedMarketingDomain.storefrontProCampaigns.nav',
          labelIdV3: 'retailerFundedMarketingDomain.storefrontProCampaigns.nav.v3',
          route: 'campaigns-storefront-pro-list',
          subRoutes: [
            'partner-campaigns-create-sfx',
            'partner-campaigns-edit',
            'partner-campaigns-view',
          ],
          accessControl: sfpCampaignAndSegmentAccessControl,
          positionNavV3: 1,
        },
        {
          type: 'item',
          llmDescription: 'Manage lifecycle marketing and customer relationship campaigns',
          labelId: 'retailerFundedMarketingDomain.lifecycleCrm.nav',
          labelIdV3: 'retailerFundedMarketingDomain.lifecycleCrm.nav.v3',
          route: 'partner-scoped-campaigns-lifecycle-crm',
          accessControl: lifecycleCrmAccessControl,
          positionNavV3: 2,
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments for all retailer-funded marketing campaigns',
          // Shared with the SFP L1 entry; SF and SFP L1s are mutually exclusive per retailer.
          labelId: 'retailerFundedMarketingDomain.segmentation.nav',
          route: 'retailer-funded-marketing-segmentation-list-storefront',
          accessControl: retailerFundedMarketingSegmentationAccessControl,
          positionNavV3: 3,
        },
        {
          type: 'item',
          llmDescription: 'Manage customer segments for Storefront Pro campaigns',
          // Mirrored under the SF L1; sfpCampaignAndSegmentAccessControl gates it to SFP users.
          labelId: 'retailerFundedMarketingDomain.storefrontPro.segmentation.nav',
          labelIdV3: 'retailerFundedMarketingDomain.storefrontPro.segmentation.nav.v3',
          route: 'segment-storefront-pro-list',
          accessControl: sfpCampaignAndSegmentAccessControl,
          positionNavV3: 4,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.StorefrontPro,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Create and manage retailer-funded marketing campaigns',
          // Shared with the SF L1 entry above.
          labelId: 'retailerFundedMarketingDomain.campaigns.phaseFour.nav',
          labelIdV3: 'retailerFundedMarketingDomain.campaigns.phaseFour.nav.v3',
          route: 'retailer-funded-marketing-site-scoped-list-storefront',
          accessControl: retailerFundedMarketingHomeNavAccessControl,
          positionNavV3: 0,
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage marketing campaigns exclusively for your custom Storefront Pro platform',
          labelId: 'retailerFundedMarketingDomain.storefrontProCampaigns.nav',
          labelIdV3: 'retailerFundedMarketingDomain.storefrontProCampaigns.nav.v3',
          route: 'campaigns-storefront-pro-list',
          subRoutes: [
            'partner-campaigns-create-sfx',
            'partner-campaigns-edit',
            'partner-campaigns-view',
          ],
          accessControl: sfpCampaignAndSegmentAccessControl,
          positionNavV3: 1,
        },
        {
          type: 'item',
          llmDescription: 'Manage lifecycle marketing and customer relationship campaigns',
          labelId: 'retailerFundedMarketingDomain.lifecycleCrm.nav',
          labelIdV3: 'retailerFundedMarketingDomain.lifecycleCrm.nav.v3',
          route: 'partner-scoped-campaigns-lifecycle-crm',
          accessControl: lifecycleCrmAccessControl,
          positionNavV3: 2,
        },
        {
          type: 'item',
          llmDescription: 'Manage customer segments for Storefront Pro campaigns',
          // v2 label kept as "Storefront Pro Segments" to avoid colliding with the
          // site-scoped "Segments" entry below; v3 uses "Multi-site segments".
          labelId: 'retailerFundedMarketingDomain.storefrontPro.segmentation.nav',
          labelIdV3: 'retailerFundedMarketingDomain.storefrontPro.segmentation.nav.v3',
          route: 'segment-storefront-pro-list',
          accessControl: sfpCampaignAndSegmentAccessControl,
          positionNavV3: 4,
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments for all retailer-funded marketing campaigns',
          // Shared with the SF L1 entry above.
          labelId: 'retailerFundedMarketingDomain.segmentation.nav',
          route: 'retailer-funded-marketing-segmentation-list-storefront',
          accessControl: retailerFundedMarketingSegmentationAccessControl,
          positionNavV3: 3,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.Marketplace,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Create and manage retailer-funded marketing campaigns',
          labelId: 'retailerFundedMarketingDomain.campaigns.phaseFour.nav',
          labelIdV3: 'retailerFundedMarketingDomain.campaigns.phaseFour.nav.v3',
          route: 'retailer-funded-marketing-site-scoped-list-marketplace',
          accessControl: retailerFundedMarketingHomeAccessControl,
          positionNavV3: 0,
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments for all retailer-funded marketing campaigns',
          labelId: 'retailerFundedMarketingDomain.segmentation.nav',
          route: 'retailer-funded-marketing-segmentation-list-marketplace',
          accessControl: retailerFundedMarketingSegmentationAccessControl,
          positionNavV3: 2,
        },
        {
          type: 'item',
          llmDescription:
            "Manage Instacart's main marketplace (eg: instacart.com) marketing campaigns for product promotions",
          // v3 label "Multi-banner campaigns" avoids collision with the single-banner entry above.
          labelId: 'retailerFundedMarketingDomain.marketplaceCampaigns.nav',
          labelIdV3: 'retailerFundedMarketingDomain.marketplaceCampaigns.nav.v3',
          route: 'campaigns-marketplace-list',
          subRoutes: [
            'partner-campaigns-create-mp',
            'partner-campaigns-edit',
            'partner-campaigns-view',
          ],
          accessControl: retailerFundedMarketingWholesalerAccessControl,
          positionNavV3: 1,
        },
        {
          type: 'item',
          llmDescription:
            'Create and manage customer segments specifically for Instacart marketplace (instacart.com) campaigns',
          // v3 label "Multi-banner segments" avoids collision with the single-banner entry above.
          labelId: 'retailerFundedMarketingDomain.marketplace.segmentation.nav',
          labelIdV3: 'retailerFundedMarketingDomain.marketplace.segmentation.nav.v3',
          route: 'segment-marketplace-list',
          accessControl: retailerFundedMarketingWholesalerAccessControl,
          positionNavV3: 3,
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      attachToV3: AdminNavEntryPointV3.Marketplace,
      subNavItems: [
        {
          route: 'app-admin-global-campaigns-campaign-moments',
          subRoutes: [
            'app-admin-global-campaigns-campaign-moments-create',
            'app-admin-global-campaigns-campaign-moments-edit',
            'app-admin-global-campaigns-campaign-moments-view',
            'app-admin-global-campaigns-campaign-moments-view-moment',
            'campaigns-create',
            'campaigns-view',
            'retailer-funded-marketing-segmentation-view',
          ],
          labelId: 'global.campaigns.title',
          accessControl: retailerFundedMarketingAdminAccessControl,
          positionNavV3: 0,
        },
      ],
    },
    {
      attachToV3: AdminNavEntryPointV3.Marketplace,
      subNavItems: [
        {
          route: 'app-admin-campaigns-list',
          subRoutes: [
            'app-admin-campaigns-create',
            'app-admin-campaigns-edit',
            'app-admin-campaigns-get',
            'app-admin-campaigns-templates',
            'app-admin-retailer-funded-marketing-campaign-templates',
            'retailer-funded-marketing-site-scoped-campaign-templates',
          ],
          labelId: 'campaignManagement.customCampaign.title',
          accessControl: retailerFundedMarketingAdminCustomCampaignAccessControl,
          positionNavV3: 1,
        },
      ],
    },
    {
      attachToV3: AdminNavEntryPointV3.Marketplace,
      subNavItems: [
        {
          route: 'app-admin-marketing-agent-calendar',
          subRoutes: ['app-admin-marketing-agent-campaigns-view'],
          labelId: 'marketingAgent.title',
          accessControl: marketingAgentCalendarUnionAccessControl,
          positionNavV3: 3,
        },
      ],
    },
  ],
}

export default definition
